import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import classes from '../SelectContractType/selectContractType.module.scss';
import confirmClasses from './confirmation.module.scss';
import { TemplateConfig } from 'services/bidAnalysis/bidAnalysisServices';
import { useStore } from 'hooks/useStore';
import { toast } from 'react-toastify';

const Confirmation = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { BidAnalysisStore } = useStore();

  const [templateConfig, setTemplateConfig] = useState<TemplateConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Read URL params and restore state
  useEffect(() => {
    const typeFromUrl = searchParams.get('type') as 'mas' | 'nc' | null;
    const oppIdFromUrl = searchParams.get('oppId');
    const productTypeFromUrl = searchParams.get('productType') as 'direct' | 'indirect' | null;

    // Restore contract type
    if (typeFromUrl && !BidAnalysisStore.selectedContractType) {
      BidAnalysisStore.setContractType(typeFromUrl);
    }

    // Restore product type
    if (productTypeFromUrl && !BidAnalysisStore.selectedProductType) {
      BidAnalysisStore.setProductType(productTypeFromUrl);
    }

    // Restore opportunity if we have oppId
    if (oppIdFromUrl && BidAnalysisStore.opportunityIterations.length > 0) {
      const opportunity = BidAnalysisStore.opportunityIterations.find(
        (opp) => opp.id.toString() === oppIdFromUrl
      );
      if (opportunity && !BidAnalysisStore.selectedOpportunity) {
        BidAnalysisStore.setSelectedOpportunity(opportunity);
      }
    }

    // Load opportunities if needed
    const contractType = BidAnalysisStore.selectedContractType || typeFromUrl;
    if (contractType && BidAnalysisStore.opportunityIterations.length === 0) {
      BidAnalysisStore.fetchOpportunityIterations(contractType).then(() => {
        // After loading, restore opportunity from URL
        if (oppIdFromUrl) {
          const opportunity = BidAnalysisStore.opportunityIterations.find(
            (opp) => opp.id.toString() === oppIdFromUrl
          );
          if (opportunity) {
            BidAnalysisStore.setSelectedOpportunity(opportunity);
          }
        }
      });
    }

    loadTemplateConfig();
  }, [searchParams, BidAnalysisStore]);

  const loadTemplateConfig = () => {
    setLoading(true);
    try {
      // Static template config based on product type - no API call needed
      const config: TemplateConfig = {
        sections: 5,
        requiredFields: 16,
        autoCalculations: 5,
        estimatedTime: '15-20 min',
      };
      setTemplateConfig(config);
    } catch (error) {
      console.error('Error loading template config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    const typeFromUrl = searchParams.get('type');
    const oppIdFromUrl = searchParams.get('oppId');
    const productTypeFromUrl = searchParams.get('productType');
    // For NC, go back to Opportunity screen (skip Product Type)
    if (typeFromUrl === 'nc') {
      navigate(`/bdportal/bid-analysis/wizard/opportunity?type=${typeFromUrl}&oppId=${oppIdFromUrl}`);
    } else {
      navigate(
        `/bdportal/bid-analysis/wizard/product-type?type=${typeFromUrl}&oppId=${oppIdFromUrl}&productType=${productTypeFromUrl}`
      );
    }
  };

  const handleCreateBidAnalysis = async () => {
    try {
      // Call the API to create bid
      const response = await BidAnalysisStore.createBid();
      
      if (response && response.bid_id) {
        setShowConfirmModal(false);
        toast.success(response.message || 'Bid created successfully!', { autoClose: 5000 });
        // Navigate to the template with the bid_id
        navigate(`/bdportal/bid-analysis/template/${response.bid_id}`);
      } else {
        console.error('Failed to create bid: No bid_id returned');
        toast.error('Failed to create bid: No bid_id returned', { autoClose: 5000 });
      }
    } catch (error: any) {
      console.error('Error creating bid analysis:', error);
      // Show error message from API or generic error
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create bid analysis';
      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  // Check if wizard data is incomplete
  // For NC, product type is not required
  const contractType = BidAnalysisStore.selectedContractType || searchParams.get('type') as 'mas' | 'nc' | null;
  const isIncomplete = !BidAnalysisStore.selectedContractType || 
                       !BidAnalysisStore.selectedOpportunity || 
                       (contractType !== 'nc' && !BidAnalysisStore.selectedProductType);

 

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#522d6e' }}></i>
        <p>Loading configuration...</p>
      </div>
    );
  }

  if (isIncomplete) {
    return (
      <div className={classes.wizardContainer}>
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <div className={classes.logo}>
              <span>gsms</span>
            </div>
            <h1>Create New Bid Analysis</h1>
          </div>
        </div>
        <div className={classes.contentContainer}>
          <div className={classes.errorContainer}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
            <h3>Incomplete Wizard Data</h3>
            <p>Please complete all previous steps before proceeding.</p>
            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <p>Missing:</p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                {!BidAnalysisStore.selectedContractType && <li>Contract Type</li>}
                {!BidAnalysisStore.selectedOpportunity && <li>Opportunity</li>}
                {contractType !== 'nc' && !BidAnalysisStore.selectedProductType && <li>Product Type</li>}
              </ul>
            </div>
            <Button 
              label="Go Back" 
              icon="pi pi-arrow-left" 
              onClick={() => navigate('/bdportal/bid-analysis/wizard/contract-type')} 
              style={{ marginTop: '1rem' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wizardContainer}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.logo}>
            <span>gsms</span>
          </div>
          <h1>Create New Bid Analysis</h1>
        </div>
      </div>

      <div className={classes.contentContainer}>
        {/* Progress Stepper */}
        <div className={classes.stepperContainer}>
          <div className={classes.stepper}>
            <div className={classes.stepLine}></div>
            <div className={classes.stepLineProgress} style={{ width: '100%' }}></div>

            <div className={`${classes.step}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                <i className="pi pi-check"></i>
              </div>
              <div className={classes.stepLabel}>Contract Type</div>
            </div>

            <div className={`${classes.step}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                <i className="pi pi-check"></i>
              </div>
              <div className={classes.stepLabel}>Opportunity List</div>
            </div>

            {contractType !== 'nc' && (
              <div className={`${classes.step}`}>
                <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                  <i className="pi pi-check"></i>
                </div>
                <div className={classes.stepLabel}>Product Type</div>
              </div>
            )}

            <div className={`${classes.step} ${classes.stepActive}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleActive}`}>{contractType === 'nc' ? '3' : '4'}</div>
              <div className={classes.stepLabel}>Confirmation</div>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <h2 className={classes.pageTitle}>Confirm Template Selection</h2>
        <p className={classes.pageSubtitle}>
          Review your selections and template configuration before creating your analysis.
        </p>

        {/* Configuration Card */}
        <div className={confirmClasses.configCard}>
          <div className={confirmClasses.configHeader}>
            <i className="pi pi-file-check"></i>
            <h3>Selected Template Configuration</h3>
          </div>

          <div className={confirmClasses.configGrid}>
            <div className={confirmClasses.configItem}>
              <div className={confirmClasses.configLabel}>Contract Type:</div>
              <div className={confirmClasses.configValue}>
                {BidAnalysisStore.selectedContractType === 'mas' 
                  ? 'MAS (Multiple Award Schedule)' 
                  : 'NC (National Contract)'}
              </div>
            </div>

            <div className={confirmClasses.configItem}>
              <div className={confirmClasses.configLabel}>Opportunity:</div>
              <div className={confirmClasses.configValue}>
                {BidAnalysisStore.selectedOpportunity?.opportunityName || 'N/A'}
              </div>
            </div>

            <div className={confirmClasses.configItem}>
              <div className={confirmClasses.configLabel}>Primary Supplier:</div>
              <div className={confirmClasses.configValue}>
                {BidAnalysisStore.selectedOpportunity?.primarySupplierId ?? 'N/A'}
              </div>
            </div>

            <div className={confirmClasses.configItem}>
              <div className={confirmClasses.configLabel}>Agency:</div>
              <div className={confirmClasses.configValue}>
                {BidAnalysisStore.selectedOpportunity?.agencies?.length
                  ? BidAnalysisStore.selectedOpportunity.agencies.join(', ')
                  : 'N/A'}
              </div>
            </div>

            {contractType !== 'nc' && (
              <div className={confirmClasses.configItem}>
                <div className={confirmClasses.configLabel}>Product Type:</div>
                <div className={confirmClasses.configValue}>
                  {BidAnalysisStore.selectedProductType === 'direct' 
                    ? 'Direct Product' 
                    : 'Indirect Product'}
                </div>
              </div>
            )}
          </div>

          {templateConfig && (
            <div className={confirmClasses.timeEstimate}>
              <div className={confirmClasses.timeValue}>{templateConfig.estimatedTime}</div>
              <div className={confirmClasses.timeLabel}>Typical Completion Time</div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={classes.actionButtons}>
          <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} outlined />
          <Button
            label="Create Bid Analysis"
            icon="pi pi-check"
            iconPos="right"
            onClick={() => setShowConfirmModal(true)}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog
        header="Create Bid Analysis"
        visible={showConfirmModal}
        style={{ width: '32rem' }}
        onHide={() => !BidAnalysisStore.isCreatingBid && setShowConfirmModal(false)}
        draggable={false}
        resizable={false}
      >
        <div className={confirmClasses.modalContent}>
          <div className={confirmClasses.modalIcon}>
            <i className="pi pi-exclamation-circle"></i>
          </div>
          <div className={confirmClasses.modalText}>
            <h3>Create bid analysis with the following configuration?</h3>
            <div className={confirmClasses.modalDetails}>
              <p>
                <strong>Contract Type:</strong>{' '}
                {BidAnalysisStore.selectedContractType === 'mas' 
                  ? 'MAS (Multiple Award Schedule)' 
                  : 'NC (National Contract)'}
              </p>
              <p>
                <strong>Opportunity:</strong> {BidAnalysisStore.selectedOpportunity?.opportunityName}
              </p>
              <p>
                <strong>Primary Supplier:</strong>{' '}
                {BidAnalysisStore.selectedOpportunity?.primarySupplierId ?? 'N/A'}
              </p>
              <p>
                <strong>Agency:</strong>{' '}
                {BidAnalysisStore.selectedOpportunity?.agencies?.length
                  ? BidAnalysisStore.selectedOpportunity.agencies.join(', ')
                  : 'N/A'}
              </p>
              {contractType !== 'nc' && (
                <p>
                  <strong>Product Type:</strong>{' '}
                  {BidAnalysisStore.selectedProductType === 'direct' 
                    ? 'Direct Product' 
                    : 'Indirect Product'}
                </p>
              )}
            </div>
            <p className={confirmClasses.modalQuestion}>Do you want to proceed?</p>
          </div>
        </div>

        <div className={confirmClasses.modalFooter}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => setShowConfirmModal(false)}
            disabled={BidAnalysisStore.isCreatingBid}
            outlined
          />
          <Button
            label="Create"
            icon="pi pi-check"
            onClick={handleCreateBidAnalysis}
            loading={BidAnalysisStore.isCreatingBid}
            autoFocus
          />
        </div>
      </Dialog>
    </div>
  );
});

export default Confirmation;
